import React, {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useMemo,
  useState
} from 'react';
import { FixedContentPortal } from '../../fixedContent/FixedContentPortal/FixedContentPortal';
import { useResizeObservedElement } from '../../hooks/useResizeObservedElement';
import { prepareProps } from '../../utils/propUtils';
import styles from './FixedButtonContainerProvider.module.css';

export const FixedButtonContainerProviderContext =
  createContext<FixedButtonContainerProviderValue>({
    containerElement: null
  });

export type FixedButtonContainerProviderValue = {
  containerElement: HTMLElement | null;
};

export type FixedButtonContainerProviderProps = PropsWithChildren<
  Record<never, never>
>;

export const FixedButtonContainerProvider: FunctionComponent<FixedButtonContainerProviderProps> =
  React.memo((props) => {
    const { children } = prepareProps({ props });

    const [containerElement, setContainerElement] =
      useState<HTMLElement | null>(null);
    const [containerElementHeight, setContainerElementHeight] = useState(0);

    const contextValue = useMemo<FixedButtonContainerProviderValue>(
      () => ({
        containerElement
      }),
      [containerElement]
    );

    useResizeObservedElement({
      element: containerElement,
      observeOptions: { box: 'border-box' },
      callback: () => {
        setContainerElementHeight(
          containerElement?.getBoundingClientRect().height ?? 0
        );
      }
    });

    return (
      <>
        <FixedButtonContainerProviderContext.Provider value={contextValue}>
          {contextValue.containerElement ? children : null}
        </FixedButtonContainerProviderContext.Provider>
        <div
          style={{ height: `${containerElementHeight}px` }}
          data-testid="FixedButtonContainerProvider.Placeholder"
        ></div>
        <FixedContentPortal>
          <div
            ref={setContainerElement}
            className={styles.buttonWrapper}
          ></div>
        </FixedContentPortal>
      </>
    );
  });
